import React, { useState } from 'react';
import { MessageSquarePlus, Send } from 'lucide-react';
import Modal from './ui/Modal';
import { useToast } from './ToastProvider';
import { useI18n } from '../utils/i18n';
import apiService from '../services/apiService';
import { collectSwDiagnostics } from '../utils/swDiagnostics';

// Feedback-Dialog (#42, Welle 2): Fehler/Wunsch mit Freitext, optionalem
// Screenshot und automatisch mitgeschicktem Kontext (Ansicht, Browser).
// Erreichbar über den Button links neben der Glocke im Header.
const FeedbackModal = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [sending, setSending] = useState(false);

  const contextUrl = `${window.location.pathname}${window.location.search}`;

  const resetAndClose = () => {
    setType('bug');
    setMessage('');
    setScreenshotFile(null);
    onClose();
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      // #256: Service-Worker-Zustand mitschicken. Ohne diese Angaben ließ sich
      // bei Meldungen zum Ladeverhalten nie sagen, ob das Gerät den aktuellen
      // Worker überhaupt angenommen hatte — die Diagnose war nur am Arbeitsplatz
      // möglich und dort meist nicht mehr reproduzierbar.
      const sw = await collectSwDiagnostics();
      await apiService.submitFeedback({
        type,
        message: message.trim(),
        contextUrl,
        contextMeta: {
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          sw
        },
        screenshotFile
      });
      pushToast({ message: t('feedback.thanks', 'Danke! Deine Rückmeldung ist angekommen.'), type: 'success' });
      resetAndClose();
    } catch (e) {
      console.error('Feedback error:', e);
      pushToast({ message: t('feedback.error', 'Feedback konnte nicht gesendet werden.'), type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={resetAndClose}
      icon={<MessageSquarePlus size={20} />}
      title={t('feedback.title', 'Feedback geben')}
      maxWidth="520px"
      closeDisabled={sending}
      closeLabel={t('common.close', 'Schließen')}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={resetAndClose} disabled={sending}>
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSend} disabled={sending || !message.trim()}>
            <Send size={16} />
            {sending ? t('feedback.sending', 'Sende…') : t('feedback.send', 'Senden')}
          </button>
        </>
      )}
    >
      <div className="form-body">
        <div className="form-group">
          <label>{t('feedback.type', 'Art der Rückmeldung')}</label>
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <input type="radio" name="feedback-type" value="bug" checked={type === 'bug'} onChange={() => setType('bug')} disabled={sending} />
              {t('feedback.typeBug', 'Fehler')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <input type="radio" name="feedback-type" value="wish" checked={type === 'wish'} onChange={() => setType('wish')} disabled={sending} />
              {t('feedback.typeWish', 'Wunsch')}
            </label>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="feedback-message">{t('feedback.message', 'Beschreibung')}</label>
          <textarea
            id="feedback-message"
            rows="6"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('feedback.messagePlaceholder', 'Was ist passiert bzw. was wünschst du dir? Je konkreter, desto besser.')}
            className="form-textarea"
            disabled={sending}
          />
        </div>
        <div className="form-group">
          <label htmlFor="feedback-screenshot">{t('feedback.screenshot', 'Screenshot (optional)')}</label>
          <input
            id="feedback-screenshot"
            type="file"
            accept="image/*"
            onChange={(e) => setScreenshotFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            disabled={sending}
          />
        </div>
        <small className="form-help">
          {t('feedback.contextHint', 'Mitgeschickt wird automatisch: aktuelle Ansicht ({{url}}), Browser und Zeitpunkt.', { url: contextUrl })}
        </small>
      </div>
    </Modal>
  );
};

export default FeedbackModal;
